import { Box, Typography, Button, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import AboutSectionImage from "../../assets/images/icons/catering-rewards/about-section-image.svg";

import PropTypes from "prop-types";
import Mail from "../../assets/images/icons/catering-rewards/mail.svg";
import Menu from "../../assets/images/icons/catering-rewards/menu.svg";
import Location from "../../assets/images/icons/catering-rewards/location.svg";
import Contact from "../../assets/images/icons/catering-rewards/contact.svg";

// Styled components
const ContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  minHeight: "600px",
  backgroundColor: "#fff",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column-reverse",
    minHeight: "auto",
  },
}));

const TextSection = styled(Box)(({ theme }) => ({
  width: "50%",
  padding: theme.spacing(8, 12),
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: "1px",
    height: "100%",
    background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.1), transparent)",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: theme.spacing(4),
    "&::after": {
      display: "none",
    },
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  width: "50%",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(45deg, rgba(0,0,0,0.1), transparent)",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    height: "300px",
  },
}));

const RestaurantImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transition: "transform 0.5s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
});

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  transition: "all 0.3s ease",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(35, 136, 86, 0.04)",
    transform: "translateX(4px)",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "36px",
  borderRadius: "8px",
  backgroundColor: "rgba(35, 136, 86, 0.08)",
  transition: "all 0.3s ease",
  "& img": {
    width: "20px",
    height: "20px",
  },
}));

const OrderButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(1.5, 4),
  borderRadius: "28px",
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 500,
  color: "white",
  background: "linear-gradient(45deg, #238856 30%, #2ab06f 90%)",
  boxShadow: "0 4px 20px rgba(35, 136, 86, 0.2)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 25px rgba(35, 136, 86, 0.3)",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "14px",
    padding: theme.spacing(1, 3),
  },
}));

const AboutSection = ({ landingData, restaurantPageDetails, restaurantImgUrl }) => {
  return (
    <ContentWrapper>
      <TextSection>
        {/* Restaurant Name and About */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 0.4,
            background: "linear-gradient(45deg, #1a1a1a, #4a4a4a)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "22px", md: "30px" },
          }}
        >
          About {restaurantPageDetails?.aboutTitle || landingData?.name}
        </Typography>

        <Typography
          sx={{
            color: "text.secondary",
            fontSize: { xs: "14px", md: "16px" },
            lineHeight: 1.8,
            mb: 2,
          }}
        >
          {restaurantPageDetails?.aboutSection?.split(/\d+\.\s*/)?.[0]}

          <Typography
            sx={{
              color: "text.primary",
              fontSize: { xs: "12px", md: "14px", marginTop: 20 },
              lineHeight: 1.8,
              mb: 1,
              fontWeight: 600,
              color: "#238856",
            }}
          >
            Our Offerings:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {restaurantPageDetails?.aboutSection
              ?.split(/\d+\.\s*/)
              ?.slice(1)
              ?.map((option, index) => (
                <Chip key={index} label={option.trim()} variant="outlined" />
              ))}
          </Box>
        </Typography>

        {/* Contact Information */}
        {restaurantPageDetails?.locationUrl && (
          <ContactItem onClick={() => window.open(restaurantPageDetails?.locationUrl, "_blank")}>
            <IconWrapper>
              <img src={Location} alt="location" />
            </IconWrapper>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>Find us on Google</Typography>
          </ContactItem>
        )}

        {landingData?.phoneNumber && (
          <ContactItem>
            <IconWrapper>
              <img src={Contact} alt="phone" />
            </IconWrapper>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              +{landingData?.phoneCountryCode}&nbsp;
              {landingData?.phoneNumber}
            </Typography>
          </ContactItem>
        )}

        {landingData?.email && (
          <ContactItem>
            <IconWrapper>
              <img src={Mail} alt="email" />
            </IconWrapper>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>
              {landingData?.email}
            </Typography>
          </ContactItem>
        )}

        {restaurantPageDetails?.menuUrl && (
          <ContactItem onClick={() => window.open(restaurantPageDetails?.menuUrl, "_blank")}>
            <IconWrapper>
              <img src={Menu} alt="menu" />
            </IconWrapper>
            <Typography sx={{ fontSize: { xs: "14px", md: "16px" } }}>Check Menu</Typography>
          </ContactItem>
        )}

        {/* Order Button */}
        {restaurantPageDetails?.orderNowUrl && (
          <OrderButton
            variant="contained"
            onClick={() => window.open(restaurantPageDetails?.orderNowUrl, "_blank")}
          >
            Order Now
          </OrderButton>
        )}
      </TextSection>

      <ImageSection>
        <RestaurantImage
          src={restaurantImgUrl || AboutSectionImage}
          alt={`${landingData?.name} atmosphere`}
        />
      </ImageSection>
    </ContentWrapper>
  );
};

export default AboutSection;

AboutSection.propTypes = {
  landingData: PropTypes.shape({
    name: PropTypes.string,
    phoneCountryCode: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
  }),
  restaurantPageDetails: PropTypes.shape({
    aboutTitle: PropTypes.string,
    aboutSection: PropTypes.string,
    locationUrl: PropTypes.string,
    menuUrl: PropTypes.string,
    orderNowUrl: PropTypes.string,
  }),
  restaurantImgUrl: PropTypes.string,
};

AboutSection.defaultProps = {
  landingData: {
    name: "",
    phoneCountryCode: "",
    phoneNumber: "",
    email: "",
  },
  restaurantPageDetails: {
    aboutSection: "",
    locationUrl: null,
    menuUrl: null,
    orderNowUrl: null,
  },
  restaurantImgUrl: null,
};
